import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError, takeUntil, tap } from 'rxjs/operators';
import { AlertService } from '../services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { HttpStatusCodes } from '../enums/httpStatuses';
import { HttpCancelService } from '../services/http-cancel.service';
import { T } from 'src/assets/i18n/translation-keys';
import { GeoLocationService } from '../services/geo-location.service';
import { AuthenticationService } from '../services/authentication.service';
import { ExternalAuthService } from '../../external/services/external-auth.service';
import { Account } from '../models/account';
import { SKIP_ERROR_HANDLING } from './skip-error-handling.context';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  account: Account;

  // External access endpoints that handle their own errors
  private externalAccessEndpoints = ['ExternalAccess', 'ExternalPolicies'];

  constructor(
    private injector: Injector,
    private router: Router,
    private readonly httpCancelService: HttpCancelService,
    private readonly authSvc: AuthenticationService,
    private readonly externalAuthService: ExternalAuthService
  ) {
    this.account = this.authSvc.getCurrentAccount();
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if this is an external endpoint - don't retry external requests
    const isExternalRequest = request.url.includes('/External');

    return next.handle(request).pipe(
      // Only retry non-external requests to avoid delays
      isExternalRequest ? tap() : retry(1),
      takeUntil(this.httpCancelService.onCancelPendingRequests()),
      catchError((error: HttpErrorResponse) => {
        if (request.context.get(SKIP_ERROR_HANDLING)) {
          return throwError(() => error);
        }
        if(!this.account) {
          this.account = this.authSvc.getCurrentAccount();
        }
        //TODO: Extract the error handling logic and make sure that it is applied FOR INNER WeTrack requests only.

        //PATCH: The following code is a patch for the current issue with the What3Words API.
        if(error.url.toLowerCase().indexOf(GeoLocationService.W3W_API_URL) > -1) {
          return throwError(() => error);
        }

        // Skip error handling for external access endpoints - they handle their own errors
        const matchesExternalAccessEndpoint = this.externalAccessEndpoints.some((endpoint) => error.url?.includes(endpoint));

        if (matchesExternalAccessEndpoint) {
          return throwError(() => error);
        }

        if (error.status === HttpStatusCodes.Unauthorized) {
          const isExternalEndpoint = error.url?.includes('/External') ?? false;

          if (isExternalEndpoint) {
            return this.externalAuthService.handleUnauthorizedError(error);
          } else {
            void this.router.navigate([`/${this.account.accountUID}/unauthorized`]);
            return throwError(() => error);
          }
        } else if (error.status === HttpStatusCodes.Forbidden) {
          if (error.url.toLowerCase().indexOf('googleapi') < 0) {
            void this.router.navigate([`/${this.account.accountUID}/access-denied`]);
            return throwError(() => error);
          }
        } else if (error.status === HttpStatusCodes.NotFound) {
          if (error.url.toLowerCase().indexOf('googleapi') < 0) {
            void this.router.navigate([`/${this.account.accountUID}/not-found`]);
          }
        } else if (error.status === HttpStatusCodes.MethodNotAllowed) {
          const isExternalEndpoint = error.url?.includes('/External') ?? false;
          if (isExternalEndpoint) {
            return this.externalAuthService.handleMethodNotAllowed(error);
          }
          return throwError(() => error);
        } else if (error.status === HttpStatusCodes.PaymentRequired) {
          void this.router.navigate([`/${this.account.accountUID}/no-access`]);
          this.httpCancelService.cancelPendingRequests();
          return throwError(() => error);
        } else {
          const alertService = this.injector.get(AlertService);
          const translateService = this.injector.get(TranslateService);

          const errorMessage = translateService.instant(T.common.reload_error_message);
          void alertService.error('', errorMessage, undefined, () => location.reload());
          return throwError(() => error);
        }
      })
    );
  }
}
